import { takeEvery, all, call, put, select } from 'redux-saga/effects'
import { actions } from './PiwikSlice'
import { arrayify } from 'src/utils'
import { replace } from 'connected-react-router'

/**
 *  Trigger a piwik request
 */
function* piwikRequest(services, { payload }) {
  const Piwik = services('Piwik')
  try {
    const { tracking, customVarUpdate } = payload

    // check for custom variable updates
    if (customVarUpdate) {
      const { key, value } = customVarUpdate

      if (!key || !value) {
        throw new Error('Piwik: Invalid custom variable update payload')
      }
      yield call([Piwik, 'updateCustomVariable'], key, value)
    }

    // Check for tracking actions
    if (tracking) {
      const { actionName, category, action, name, customData = null } = tracking

      if (actionName) {
        yield call([Piwik, 'trackAction'], actionName, customData)
      } else if (category && action && name) {
        yield call([Piwik, 'track'], category, action, name, customData || null)
      } else {
        throw new Error('Piwik: Invalid tracking payload')
      }
    }

    yield put(actions.piwikSuccess())
  } catch (e) {
    console.log(e)
    yield put(actions.piwikError({ error: e.message }))
  }
}

/**
 *  Piwik tracking when it comes from the newsletter
 */
function* handleNewsletterPiwikTracking(
  services,
  { payload: newsletterTrackingRequests }
) {
  const queryString = services('queryString')
  try {
    const newsletterEventId = queryString.get('nle')
    const newsletterCampaignId = queryString.get('nlc')
    const newletterFrom = queryString.get('from')

    const location = yield select(state => state.router.location)

    // Check that it comes from a newsletter based on path params
    if (newsletterEventId && newsletterCampaignId && newletterFrom) {
      const trackingDetails = newsletterTrackingRequests[newletterFrom]

      // Get tracking info mapping
      if (trackingDetails) {
        const arrayfiedTrackingRequests = arrayify(
          trackingDetails.trackingDetails
        )
        if (
          trackingDetails?.validation === undefined ||
          trackingDetails.validation(location, newletterFrom)
        ) {
          for (const trackingPayload of arrayfiedTrackingRequests) {
            yield call(piwikRequest, services, { payload: trackingPayload })
          }
        }
        // Remove 'from' path param
        const newQS = queryString.removeKey('from')
        yield put(replace({ search: newQS }))
      }
    }
  } catch (e) {
    console.log(e)
  }
}

/**
 * Handle a “track on site” request by calling Piwik.trackOnSite and
 * dispatching success or error.
 *
 * @param {Object} payload
 *                 The payload carried by the action
 * @param {Object} payload.tracking
 *                 Tracking parameters for this request
 * @param {number} payload.tracking.siteId
 *                 site ID to send this event to.
 * @param {string} payload.tracking.actionName
 *                 The action name to record.
 * @param {string} payload.tracking.memberId
 *                 The IDH member id to record.
 * @param {string} payload.tracking.emailId
 *                 The IDH email id value to record.
 * @param {Object} [payload.tracking.customData=null]
 *                 Optional extra data to include with the event.
 */
function* handleOngageTrackingRequest(services, { payload }) {
  const Piwik = services('Piwik')
  try {
    const { tracking } = payload

    const Session = services('Session')
    const user = yield call([Session, 'userLoad'])

    // Check for tracking actions
    if (tracking) {
      const {
        siteId,
        actionName,
        memberId,
        emailId,
        customData = null
      } = tracking

      if (!siteId || !actionName || !memberId || !emailId) {
        throw new Error('Piwik: Invalid tracking payload')
      }

      if (!user) {
        yield call([Piwik, 'updateCustomVariable'], 'MemberID', memberId)
        yield call([Piwik, 'updateCustomVariable'], 'EmailID', emailId)
      }

      yield call([Piwik, 'trackOnSite'], siteId, actionName, customData)

      if (!user) {
        yield call([Piwik, 'updateCustomVariable'], 'MemberID', 0)
        yield call([Piwik, 'updateCustomVariable'], 'EmailID', 0)
      }
    }

    yield put(actions.piwikSuccess())
  } catch (e) {
    yield put(actions.piwikError({ error: e.message }))
  }
}

export default function* watchTracking(services) {
  yield all([takeEvery(actions.piwikRequest, piwikRequest, services)])
  yield all([
    takeEvery(
      actions.newsletterPiwikRequest,
      handleNewsletterPiwikTracking,
      services
    )
  ])
  yield all([
    takeEvery(
      actions.trackOngagePiwikRequest,
      handleOngageTrackingRequest,
      services
    )
  ])
}
